import {
  TriggerCreate,
  TriggerForEditor,
  TriggerStepCreate,
  TriggerStepIf,
  When,
} from "@dots-projects/types";
import { Edge, Node } from "reactflow";
import { FlowNodeType } from "../TriggerFlow";
import { TriggerEditorState } from "./reducerTypes";

export type PlaceholderNode = Omit<
  Node<{ parentStepId: string; when?: When }>,
  "type"
> & { type: "placeholder" };

export type PlaceholderData = {
  parentStepId: string;
  isParentTriggerRoot: boolean;
  when?: When;
  transitionIndex?: number;
  isFloating?: boolean;
  isFirstPlaceholder?: boolean;
  draggable?: boolean; // Use this field to pass `draggable` into TriggerFlow.PlaceholderNode, since it's not available as one of the props
};

export type ConditionNode = Omit<
  Node<{ label?: string; stepId: string }>,
  "type"
> & { type: "condition" };

export type IfElseNode = Omit<
  Node<{ if: TriggerStepIf; trigger: TriggerForEditor }>,
  "type"
> & {
  type: "if-else";
};

export type TypedNode =
  | (Omit<Node<any>, "type"> & {
      type: Exclude<FlowNodeType, "placeholder" | "condition" | "if-else">;
    })
  | PlaceholderNode
  | ConditionNode
  | IfElseNode;

export function getNode(nodes: Node[], nodeId: string) {
  return nodes.find((n) => n.id === nodeId) as TypedNode;
}

export function getSelectedNode(
  state: TriggerEditorState
): TypedNode | undefined {
  return state.flow.nodes.find((n) => n.selected) as TypedNode;
}

export function getSelectedTransition(state: TriggerEditorState) {
  return state.flow.edges.find((e) => e.selected);
}

function getParentId(nodeId: string, edges: Edge[]) {
  const edge = edges.find((e) => e.target === nodeId);
  return edge?.source;
}

export function getSelectedStep(state: TriggerEditorState) {
  const node = getSelectedNode(state);
  switch (node?.type) {
    case "step":
      return state.trigger.current.steps.find((s) => s.id === node.id);
    case "condition":
      const parentStepId = node.data.stepId;
      return state.trigger.current.steps.find((s) => s.id === parentStepId);
  }
  return undefined;
}

/**
 *
 * @param n1
 * @param n2
 * @returns true if the two nodes overlap, false otherwise
 */
export function isOverlap(n1: Node, n2: Node) {
  // This offset is used to make sure two nodes are not considered overlapping and the same time the reason is
  // The step node has a 180px height but for some reason react flow render the node with a height of 124px instead of 180px
  // so we are getting 65 px of blank space at the bottom of the node causing the overlap detection to fail, so we need to subtract that remainder
  // from the height. We subtract 50px instead of 65px to give it a little of buffer
  const bottomOffset = 50;
  const node2Rec = (n: Node) => ({
    left: n.position.x,
    right: n.position.x + (n.width ?? 0),
    top: n.position.y,
    bottom: n.position.y + (n.height ?? 0) - bottomOffset,
  });
  const r1 = node2Rec(n1),
    r2 = node2Rec(n2);
  //https://silentmatt.com/rectangle-intersection/

  return (
    r1.left < r2.right &&
    r1.right > r2.left &&
    r1.top < r2.bottom &&
    r1.bottom > r2.top
  );
}
